import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { Row, Col } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { SectionLayout } from 'components/Sections/SectionLayout';
import Button from 'components/Button';
import MarketoForm from 'components/Form/MarketoForm';
import { WebsiteImage } from 'components/TitleDescription';
import Video from 'components/Video';
import EmbedCode from 'components/EmbedCode';
import getFluidGatsbyImage from 'hooks/getFluidGatsbyImage';
import Text from 'components/Text';
import { getRTInlineMarketingFormProps } from 'utils/marketo';

//use these styles if there is no featured content
const noFeatureStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
};

const TwoColumnLayout = ({
  anchorId,
  columnOne,
  columnTwo,
  backgroundColor,
  leftAlignContent,
  cornerImage,
  pageType,
  slug,
  ...otherProps
}) => {
  if (leftAlignContent) noFeatureStyle.alignItems = 'start';

  const bgColor = backgroundColor?.backgroundColor;
  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: text => <Italic>{text}</Italic>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        return (
          <Text
            type="p"
            className={'blocks-paragraph'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING]: (node, children) => {
        return (
          <Text
            type="h1"
            className={'blocks-heading-1'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_2]: (node, children) => {
        return (
          <Text
            type="h2"
            className={' blocks-heading-2'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            size={'h4'}
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        return (
          <Text
            className={'blocks-heading-3'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            size={'h2'}
            type="h3"
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_4]: (node, children) => {
        return (
          <Text
            className={'blocks-heading-4'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            size={'h2'}
            type="h4"
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_5]: (node, children) => {
        return (
          <Text
            type="h5"
            className={'blocks-heading-5'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_6]: (node, children) => {
        return (
          <Text
            type="h6"
            className={'blocks-heading-6'}
            color={bgColor && bgColor !== 'White' && bgColor !== 'Alice Blue' ? 'white' : 'black'}
            style={{ textAlign: leftAlignContent && 'left' }}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        const type = node?.data?.target?.sys?.contentType?.sys?.id;
        const component = node.data.target;
        if (type === 'inlineMarketingForm') {
          return (
            <MarketoForm
              dataPosition="2 Column"
              shadow={bgColor === 'White'}
              slug={slug}
              {...getRTInlineMarketingFormProps(component.fields)}
            />
          );
        } else if (type === 'embed') {
          return (
            <EmbedCode
              code={component?.fields?.embedCode['en-US']}
              headCode={component?.fields?.headCode ? component?.fields?.headCode['en-US'] : null}
            />
          );
        } else if (type === 'callToAction') {
          let buttonLabel = component?.fields?.buttonLabel['en-US'];
          let url = component?.fields?.url['en-US'];
          return (
            <Row>
              <Col span={14} offset={5}>
                <EmbeddedCta
                  data-position="Body - 2 Column Layout CTA"
                  className="test-embedded-cta"
                  href={url}
                >
                  <Button width="100%">{buttonLabel}</Button>
                </EmbeddedCta>
              </Col>
            </Row>
          );
        } else if (type === 'blockQuote') {
          const quote = component?.fields?.quote['en-US'];
          return <BlockQuote>{quote}</BlockQuote>;
        } else if (type === 'image') {
          const { contentType, url, details } = component?.fields?.image['en-US']?.fields?.file[
            'en-US'
          ];
          const fluidsrc = {
            fluid: getFluidGatsbyImage(
              { file: { contentType, url, details } },
              { toFormat: 'webp' }
            ),
          };
          if (contentType === 'video/mp4') {
            const boxShadow = component.fields.boxShadow && component.fields.boxShadow['en-US'];

            return (
              <Video
                boxShadow={boxShadow}
                type="preview-only"
                height="300px"
                width="100%"
                mainSrc={url}
                previewSrc={url}
                style={{ position: 'relative', right: '0px', top: '0px' }}
              />
            );
          } else if (contentType.includes('image')) {
            const title =
              (component?.fields?.alt && component?.fields?.alt['en-US']) ||
              component?.fields?.image['en-US']?.fields?.title['en-US'];
            return (
              <WebsiteImage
                src={fluidsrc}
                alt={title}
                marginTop="0px"
                style={{ width: '100%', height: 'auto', maxWidth: '100%', maxHeight: '400px' }}
              />
            );
          }
        }
      },
    },
  };

  let reverseInMobile = false;
  if (columnTwo) {
    columnOne?.json.content.forEach(contentItem => {
      if (contentItem?.data?.target?.sys?.contentType?.sys?.id === 'image') {
        reverseInMobile = true;
      }
    });
  }
  return (
    <SectionLayout
      backgroundColor={bgColor}
      backgroundImageTypeWithPosition={cornerImage}
      contentBelowSection={otherProps?.contentBelowSection}
      id={anchorId}
    >
      <SectionRow
        type="flex"
        justify="center"
        align="middle"
        gutter={80}
        $reverseInMobile={reverseInMobile}
      >
        <SectionCol
          lg={columnTwo ? 12 : 24}
          md={columnTwo ? 12 : 24}
          sm={columnTwo ? 12 : 24}
          xs={24}
          style={!columnTwo && noFeatureStyle}
          className={pageType === 'terms' && 'row-content-terms--col'}
        >
          {columnOne && documentToReactComponents(columnOne.json, options)}
        </SectionCol>
        {columnTwo && (
          <SectionCol lg={12} md={12} sm={12} xs={24}>
            {columnTwo && documentToReactComponents(columnTwo.json, options)}
          </SectionCol>
        )}
      </SectionRow>
    </SectionLayout>
  );
};

TwoColumnLayout.propTypes = {
  columnOne: PropTypes.object.isRequired,
};

const SectionRow = styled(Row)`
  margin-left: 0px !important;
  margin-right: 0px !important;

  @media (max-width: 576px) {
    flex-direction: ${props => (props.$reverseInMobile ? 'column-reverse' : 'column')};
  }
`;

const SectionCol = styled(Col)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;

  &.row-content-terms--col {
    padding-top: 120px;
    text-align: left !important;
    width: 100%;

    h2 {
      font-size: 36px;
      padding-bottom: 40px;
    }

    h3 {
      font-size: 28px;
      width: 100%;
      margin-top: 20px;
      padding-bottom: 20px;
    }

    h5 {
      font-size: 25px;
      width: 100%;
      margin-top: 20px;
      padding-bottom: 20px;
    }

    h6 {
      font-size: 15px;
      font-weight: normal;
      color: red;
      margin-top: 0;
      margin-bottom: 16px;
    }

    .blocks-paragraph {
      font-size: 16px;
      font-weight: normal;
      margin-top: 0;
      margin-bottom: 16px;
    }
  }

  @media (max-width: 991px) {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  @media (max-width: 576px) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    width: 100%;
  }
`;

export default TwoColumnLayout;

const Bold = ({ children }) => (
  <span className="bold">
    <b>{children}</b>
  </span>
);
const Italic = ({ children }) => (
  <span className="bold">
    <em>{children}</em>
  </span>
);

const EmbeddedCta = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlockQuote = styled.p`
  font-weight: 900;
  font-size: 25px;
  line-height: 120%;
  letter-spacing: -0.02em;
  color: var(--normal-description-color);
  text-align: left;
  max-width: 751px;
  padding: 41px 33px;
  box-shadow: 0px 15px 40px rgba(11, 26, 33, 0.1);
  background: var(--white-color);
  margin: 46px auto 61px;
  position: relative;

  &:before {
    content: ' ';
    width: 16px;
    height: 60px;
    background: var(--secondary-green-background);
    left: -16px;
    top: -16px;
    position: absolute;
  }

  &:after {
    content: ' ';
    width: 60px;
    height: 16px;
    background: var(--secondary-green-background);
    left: -16px;
    top: -16px;
    position: absolute;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    padding: 24px;
    max-width: 466px;
    margin-top: 46px;
    margin-bottom: 40px;

    &:before {
      width: 14px;
      height: 50px;
      left: -14px;
      top: -14px;
    }

    &:after {
      width: 50px;
      height: 14px;
      left: -14px;
      top: -14px;
    }
`;
